import { useIntl } from "react-intl";

import FormField from "components/molecules/FormField/FormField";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { dimensionUnitOptions, weightUnitOptions } from "constants/Common";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { QuotationFreightModeOption } from "types/Common";
import {
  getChargeableWeight,
  getDimensionalWeight,
  getPackingListCalculations,
  getServiceInputVisibility,
} from "helpers/quotationFormHelper";
import usePermissions from "hooks/usePermissions";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";

interface ServiceFormSidebarProps {
  selectedFreightModeObject?: QuotationFreightModeOption;
  quoteRequestDetails: any;
}

export default function ServiceFormInputs({ selectedFreightModeObject, quoteRequestDetails }: ServiceFormSidebarProps) {
  const intl = useIntl();
  const { setValue } = useFormContext();
  const { permissions } = usePermissions();
  const { unit_system_preferences } = JSON.parse(permissions ?? "{}");

  const { totalWeight, weightUnit } = getPackingListCalculations(quoteRequestDetails) ?? {};

  const { showChargeableWeight, showRoadFreightType, showMaxDimensions, showMaxWeight, showMaxAllowedWeight } =
    getServiceInputVisibility(selectedFreightModeObject);

  const userPreferredWeightUnit = unit_system_preferences?.split("-")?.[1];

  const defaultChargeableWeight = getChargeableWeight(
    +(totalWeight?.replace(/,/g, "") || 0),
    getDimensionalWeight(quoteRequestDetails?.organization?.good_items, weightUnit),
    {
      inputUnit: weightUnit,
      outputUnit: userPreferredWeightUnit,
    },
  );

  const defaultChargeableWeightUnit =
    defaultChargeableWeight > 0 && userPreferredWeightUnit
      ? userPreferredWeightUnit
      : weightUnit || quoteRequestDetails?.organization?.good_items?.[0]?.w_unit || weightUnitOptions[0].value;

  useEffect(() => {
    setValue("service.af_chargeable_wt", defaultChargeableWeight);
  }, [defaultChargeableWeight]);

  useEffect(() => {
    if (showChargeableWeight) setValue("service.af_chargeable_wt_unit", defaultChargeableWeightUnit);
    if (showMaxWeight) setValue("service.rf_max_wt_unit", defaultChargeableWeightUnit);
    if (showMaxAllowedWeight) setValue("service.obc_max_wt_allowed_unit", defaultChargeableWeightUnit);
  }, [defaultChargeableWeightUnit]);

  return (
    <>
      {showRoadFreightType && (
        <div className="w-192 mb-4">
          <SearchSelect
            label={intl.formatMessage({
              id: "quotation-form.roadFreightType",
              defaultMessage: "Road Freight Type",
            })}
            defaultValue={selectedFreightModeObject?.options?.[0]?.value}
            options={selectedFreightModeObject?.options ?? []}
            name="service.rf_type"
            placeholder="Please select"
            required
            shouldUnregister
          />
        </div>
      )}

      {showChargeableWeight && (
        <div className="d-flex">
          <div className="w-192">
            <FormField
              id="service.af_chargeable_wt"
              field_id="service.af_chargeable_wt"
              label={intl.formatMessage({
                id: "quotation-form.chargeableWeight",
                defaultMessage: "Chargeable Weight",
              })}
              mandatory
              eventContext="Provider Quotation Form"
              type="textinput"
              placeholder={intl.formatMessage({
                id: "quotation-form.enterWeight",
                defaultMessage: "Enter weight",
              })}
              validations={{
                validate: {
                  onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                },
                setValueAs: (v: string) => (!v ? null : v),
                shouldUnregister: true,
              }}
              {...(defaultChargeableWeight > 0 && {
                defaultValue: defaultChargeableWeight.toString(),
              })}
            />
          </div>
          <div>
            <Dropdown
              useHookForm
              name="service.af_chargeable_wt_unit"
              defaultValue={defaultChargeableWeightUnit}
              options={weightUnitOptions}
              className="unit-dropdown"
              required
              shouldUnregister
            />
          </div>
        </div>
      )}

      <div className="d-flex">
        {showMaxWeight && (
          <>
            <div className="w-192">
              <FormField
                id="service.rf_max_wt"
                field_id="service.rf_max_wt"
                label={intl.formatMessage({
                  id: "quotation-form.chargeableWeight",
                  defaultMessage: "Chargeable Weight",
                })}
                eventContext="Provider Quotation Form"
                type="textinput"
                placeholder={intl.formatMessage({
                  id: "quotation-form.enterWeight",
                  defaultMessage: "Enter weight",
                })}
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                  shouldUnregister: true,
                }}
              />
            </div>
            <div>
              <Dropdown
                useHookForm
                name="service.rf_max_wt_unit"
                defaultValue={defaultChargeableWeightUnit}
                options={weightUnitOptions}
                className="unit-dropdown"
                shouldUnregister
              />
            </div>
          </>
        )}
        {showMaxDimensions && (
          <>
            <div className="w-192">
              <FormField
                id="service.rf_max_dimensions"
                field_id="service.rf_max_dimensions"
                label={intl.formatMessage({
                  id: "quotation-form.maximumDimensions",
                  defaultMessage: "Maximum dimensions",
                })}
                eventContext="Provider Quotation Form"
                type="textinput"
                placeholder={intl.formatMessage({
                  id: "quotation-form.enterDimensions",
                  defaultMessage: "Enter dimensions",
                })}
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                  shouldUnregister: true,
                }}
              />
            </div>
            <Dropdown
              useHookForm
              name="service.rf_max_dimensions_unit"
              defaultValue={dimensionUnitOptions[0].value}
              options={dimensionUnitOptions}
              className="unit-dropdown"
              shouldUnregister
            />
          </>
        )}
      </div>

      <div className="d-flex">
        {showMaxAllowedWeight && (
          <>
            <div className="w-192">
              <FormField
                id="service.obc_max_wt_allowed"
                field_id="service.obc_max_wt_allowed"
                label={intl.formatMessage({
                  id: "quotation-form.maximumAllowedWeightPerItem",
                  defaultMessage: "Maximum allowed weight per item",
                })}
                eventContext="Provider Quotation Form"
                mandatory
                type="textinput"
                placeholder={intl.formatMessage({
                  id: "quotation-form.enterWeight",
                  defaultMessage: "Enter weight",
                })}
                validations={{
                  validate: {
                    onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
                  },
                  setValueAs: (v: string) => (!v ? null : v),
                  shouldUnregister: true,
                }}
              />
            </div>
            <div>
              <Dropdown
                useHookForm
                name="service.obc_max_wt_allowed_unit"
                defaultValue={defaultChargeableWeightUnit}
                options={weightUnitOptions}
                className="unit-dropdown"
                shouldUnregister
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}

ServiceFormInputs.defaultProps = {
  selectedFreightModeObject: undefined,
};
